import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";
import {
    FaPaperPlane,
    FaUser,
    FaLock,
    FaRegClock,
    FaExclamationCircle,
    FaPaperclip,
    FaTimes,
    FaFileAlt,
    FaCheck,
    FaCheckDouble,
    FaChevronDown,
} from "react-icons/fa";
import { doubleTick, whtsappBg, whtsappIcon } from "../../asset";
import { AppDispatch } from "../../store";
import {
    fetchChatMessages,
    sendWhatsappMessage,
    WhatsappMessageType,
} from "../../features/message/Messageservice";
import {
    ChatEvent,
    LiveState,
    samePhone,
    subscribeChatEvents,
} from "../../features/live-chat/chatEvents";
import { avatarColor, formatPhone } from "./LiveChatContacts";
import ChatMedia, { ChatMediaInfo, getMediaKind } from "./ChatMedia";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
    selectedPhone: string;
}

type DeliveryStatus = "sent" | "delivered" | "read" | "failed";

interface ChatUIMessage {
    id: number;
    text: string;
    type: "sent" | "received";
    time: string;
    date: Date;
    // Only for messages typed here that aren't confirmed by the server yet
    status?: "pending" | "failed";
    // WhatsApp delivery status from the API (sent messages only)
    delivery?: DeliveryStatus;
    // Image / video / audio / document
    media?: ChatMediaInfo;
}

const PAGE_SIZE = 50;

// Gallery item → attachment
const galleryToMedia = (item: any): ChatMediaInfo => {
    const t = String(item.mediaType || "").toUpperCase();
    return {
        kind: t === "VIDEO" ? "video" : t === "DOCUMENT" ? "document" : "image",
        mediaId: item.mediaId || item.mediaUrl,
        mediaUrl: item.mediaUrl,
        fileName: item.fileName || item.templateName,
        mimeType: item.mimeType,
    };
};

// Attachment kind → API "type"
const toApiType = (kind?: string): WhatsappMessageType =>
    kind === "video" ? "VIDEO" : kind === "document" ? "DOCUMENT" : kind === "image" ? "IMAGE" : "TEXT";

// API "status" (SENT / DELIVERED / READ / FAILED) → tick state
const toDelivery = (status?: string | null): DeliveryStatus => {
    const s = String(status || "").toUpperCase();
    if (s === "READ") return "read";
    if (s === "DELIVERED") return "delivered";
    if (s === "FAILED") return "failed";
    return "sent";
};

const toUI = (msg: any): ChatUIMessage => {
    // API sends seconds (e.g. 1790592802) or ms (e.g. 1790597326490) — handle both
    const ts = Number(msg.timestamp) || 0;
    const date = new Date(ts < 1e12 ? ts * 1000 : ts);

    const kind = getMediaKind(msg.messageType, msg.mimeType);
    const hasMedia = !!kind && !!(msg.mediaId || msg.mediaUrl);

    return {
        id: msg.id,
        // For media, show only the caption (file name is shown on the card)
        text: hasMedia ? msg.caption || "" : msg.message || "",
        type: msg.direction === "OUTBOUND" ? "sent" : "received",
        date,
        time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        delivery: toDelivery(msg.status || msg.deliveryStatus),
        media: hasMedia
            ? {
                  kind: kind!,
                  mediaId: msg.mediaId,
                  mediaUrl: msg.mediaUrl,
                  fileName: msg.fileName,
                  mimeType: msg.mimeType,
              }
            : undefined,
    };
};

// Merge + remove duplicates by id + sort oldest → newest
const mergeMessages = (a: ChatUIMessage[], b: ChatUIMessage[]) => {
    const map = new Map<number, ChatUIMessage>();
    [...a, ...b].forEach((m) => map.set(m.id, m));
    return Array.from(map.values()).sort((x, y) => x.id - y.id);
};

const dayLabel = (d: Date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return "TODAY";
    if (d.toDateString() === yesterday.toDateString()) return "YESTERDAY";

    return d.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Little bubble "tail" like WhatsApp
const Tail = ({ side }: { side: "left" | "right" }) => (
    <svg
        viewBox="0 0 8 13"
        width="8"
        height="13"
        className={`absolute top-0 ${side === "right" ? "-right-2 text-[#d9fdd3]" : "-left-2 text-white scale-x-[-1]"
            }`}
    >
        <path fill="currentColor" d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z" />
    </svg>
);

// ✓ sent (gray) · ✓✓ delivered (gray) · ✓✓ read (blue) · ⚠ failed · 🕒 sending
const Ticks = ({ msg }: { msg: ChatUIMessage }) => {
    if (msg.status === "pending") {
        return <FaRegClock className="text-[10px] text-[#8696a0]" aria-label="Sending" />;
    }
    if (msg.status === "failed") return null; // "Tap to retry" is shown below the bubble text

    switch (msg.delivery) {
        case "read":
            return <img src={doubleTick} className="w-3 h-3" alt="Read" />;
        case "delivered":
            return <FaCheckDouble className="text-[11px] text-[#8696a0]" aria-label="Delivered" />;
        case "failed":
            return <FaExclamationCircle className="text-[11px] text-[#ea0038]" aria-label="Failed" />;
        default:
            return <FaCheck className="text-[10px] text-[#8696a0]" aria-label="Sent" />;
    }
};

export default function LiveChatWindow({ selectedPhone }: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();

    const [messages, setMessages] = useState<ChatUIMessage[]>([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasOlder, setHasOlder] = useState(false);
    const [attachment, setAttachment] = useState<ChatMediaInfo | null>(null);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [live, setLive] = useState<LiveState>("connecting");

    // Paging state (refs so the scroll observer always sees latest values)
    const nextOlderPageRef = useRef(0);     // page number that holds older messages
    const directionRef = useRef<1 | -1>(1); // +1 = API returns newest first, -1 = oldest first
    const totalPagesRef = useRef<number | null>(null);
    const hasOlderRef = useRef(false);
    const loadingOlderRef = useRef(false);
    const requestIdRef = useRef(0);         // ignores responses for a previous contact

    // Scroll handling
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const topSentinelRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    // bottom = jump to the latest · keep = older ones added on top · stay = don't move (reading history)
    const scrollModeRef = useRef<"bottom" | "keep" | "stay">("bottom");
    const prevScrollHeightRef = useRef<number | null>(null);

    const fetchPage = (page: number) =>
        dispatch(fetchChatMessages({ mobile: selectedPhone, page, size: PAGE_SIZE })).unwrap();

    const setOlder = (value: boolean) => {
        hasOlderRef.current = value;
        setHasOlder(value);
    };

    /* ---------- First load: latest 50 messages ---------- */
    const loadChat = async () => {
        const requestId = ++requestIdRef.current;

        setMessages([]);
        setOlder(false);
        loadingOlderRef.current = false;
        setLoadingOlder(false);

        if (!selectedPhone) return;

        setLoading(true);

        try {
            const first = await fetchPage(0);
            if (requestId !== requestIdRef.current) return;

            let batch: any[] = first.results || [];
            const totalPages: number | null = first.page?.count ?? null;
            totalPagesRef.current = totalPages;

            // Work out whether page 0 holds the newest or the oldest messages
            const newestFirst =
                batch.length < 2 || batch[0].id > batch[batch.length - 1].id;

            if (newestFirst) {
                // page 0 = latest, older = page 1, 2, 3…
                directionRef.current = 1;
                nextOlderPageRef.current = 1;
                setOlder(totalPages != null ? totalPages > 1 : batch.length === PAGE_SIZE);
            } else {
                // page 0 = oldest → the latest messages are on the LAST page
                directionRef.current = -1;

                if (totalPages != null && totalPages > 1) {
                    const last = await fetchPage(totalPages - 1);
                    if (requestId !== requestIdRef.current) return;

                    batch = last.results || [];
                    nextOlderPageRef.current = totalPages - 2;
                    setOlder(true);
                } else {
                    setOlder(false);
                }
            }

            scrollModeRef.current = "bottom";
            // Keep anything that arrived live while this was loading
            setMessages((prev) => mergeMessages(prev, batch.map(toUI)));
        } catch (error) {
            console.error("Failed to load chat", error);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    /* ---------- Scroll to top: load 50 older messages ---------- */
    const loadOlder = useCallback(async () => {
        if (loadingOlderRef.current || !hasOlderRef.current || !selectedPhone) return;

        const requestId = requestIdRef.current;
        const page = nextOlderPageRef.current;

        loadingOlderRef.current = true;
        setLoadingOlder(true);

        try {
            const result = await fetchPage(page);
            if (requestId !== requestIdRef.current) return;

            const batch: any[] = result.results || [];
            const next = page + directionRef.current;
            const totalPages = totalPagesRef.current;

            nextOlderPageRef.current = next;

            if (directionRef.current === 1) {
                setOlder(totalPages != null ? next < totalPages : batch.length === PAGE_SIZE);
            } else {
                setOlder(next >= 0);
            }

            // Remember height so the view doesn't jump when older messages are added on top
            prevScrollHeightRef.current = scrollRef.current?.scrollHeight ?? null;
            scrollModeRef.current = "keep";

            setMessages((prev) => mergeMessages(prev, batch.map(toUI)));
        } catch (error) {
            console.error("Failed to load older messages", error);
        } finally {
            if (requestId === requestIdRef.current) {
                loadingOlderRef.current = false;
                setLoadingOlder(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPhone]);

    useEffect(() => {
        setMessage("");
        setAttachment(null);
        loadChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPhone]);

    // Media picked in Gallery comes back here
    useEffect(() => {
        const picked = (location.state as any)?.selectedMedia;
        if (!picked) return;

        setAttachment(galleryToMedia(picked));
        // Clear route state so it isn't attached again on refresh
        navigate(location.pathname, { replace: true, state: {} });
        inputRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const openGallery = () => {
        navigate("/admin/gallery", {          // ← change if your gallery route is different
            state: {
                mode: "select",
                returnTo: location.pathname,
                returnState: { phone: selectedPhone },
            },
        });
    };

    // Scroll: bottom on first load / new message, keep position when older ones are prepended
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        if (scrollModeRef.current === "keep" && prevScrollHeightRef.current != null) {
            el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
            prevScrollHeightRef.current = null;
        } else if (scrollModeRef.current === "stay") {
            // a live message arrived while the user is reading older ones: leave them there
        } else {
            el.scrollTop = el.scrollHeight;
        }

        scrollModeRef.current = "bottom";
    }, [messages]);

    // Watch the top of the list; when it comes into view, load older messages.
    // Re-observing after each load keeps loading if the chat is still too short to scroll.
    useEffect(() => {
        const el = topSentinelRef.current;
        if (!el || !hasOlder) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadOlder();
            },
            { root: scrollRef.current, rootMargin: "150px 0px 0px 0px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [loadOlder, hasOlder, messages.length]);

    // Auto-grow textarea up to ~5 lines
    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [message]);

    // Insert date chips between days
    const rows = useMemo(() => {
        const out: Array<
            | { kind: "date"; key: string; label: string }
            | { kind: "msg"; key: string; msg: ChatUIMessage; first: boolean }
        > = [];
        let lastDay = "";
        let lastType: ChatUIMessage["type"] | null = null;

        messages.forEach((m) => {
            const day = m.date.toDateString();
            if (day !== lastDay) {
                out.push({ kind: "date", key: `d-${day}`, label: dayLabel(m.date) });
                lastDay = day;
                lastType = null;
            }
            out.push({ kind: "msg", key: `m-${m.id}`, msg: m, first: m.type !== lastType });
            lastType = m.type;
        });

        return out;
    }, [messages]);

    /* ---------- Reload latest page (after sending) ---------- */
    // Replaces the temporary message with the real one saved by the server
    const refreshLatest = async (removeTempId: number) => {
        const requestId = requestIdRef.current;

        const first = await fetchPage(0);
        if (requestId !== requestIdRef.current) return;

        let batch: any[] = first.results || [];
        const totalPages: number | null = first.page?.count ?? null;

        // API returns oldest first → latest messages are on the last page
        if (directionRef.current === -1 && totalPages != null && totalPages > 1) {
            const oldTotal = totalPagesRef.current;
            const last = await fetchPage(totalPages - 1);
            if (requestId !== requestIdRef.current) return;
            batch = last.results || [];

            // A new page was created → also grab the one before it
            if (oldTotal != null && totalPages > oldTotal) {
                const prevPage = await fetchPage(totalPages - 2);
                if (requestId !== requestIdRef.current) return;
                batch = [...(prevPage.results || []), ...batch];
            }
        }

        totalPagesRef.current = totalPages;
        scrollModeRef.current = "bottom";

        setMessages((prev) =>
            mergeMessages(
                prev.filter((m) => m.id !== removeTempId),
                batch.map(toUI)
            )
        );
    };

    /* ---------- Live updates: new messages and ticks arrive as they happen ---------- */
    const selectedRef = useRef(selectedPhone);
    selectedRef.current = selectedPhone;
    const refreshRef = useRef(refreshLatest);
    refreshRef.current = refreshLatest;

    useEffect(
        () =>
            subscribeChatEvents({
                onState: setLive,
                // Back online: pick up anything sent while the connection was down
                onResync: () => {
                    if (selectedRef.current) refreshRef.current(-1).catch(() => {});
                },
                onMessage: (row: ChatEvent) => {
                    if (!samePhone(row.mobile, selectedRef.current)) return;
                    const msg = toUI(row);
                    const el = scrollRef.current;
                    const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 300;

                    setMessages((prev) => {
                        // A status change for a message that isn't loaded (older page): nothing to show
                        if (!row.created && !prev.some((m) => m.id === msg.id)) return prev;

                        let base = prev;
                        // Our own message came back from the server: it replaces the "sending" copy
                        if (row.created && msg.type === "sent") {
                            const i = prev.findIndex((m) => m.status === "pending" && m.text === msg.text);
                            if (i >= 0) base = prev.filter((_, j) => j !== i);
                        }
                        scrollModeRef.current = nearBottom ? "bottom" : "stay";
                        return mergeMessages(base, [msg]);
                    });
                },
            }),
        []
    );

    /* ---------- Send (text, or media with optional caption) ---------- */
    const handleSend = async (retry?: ChatUIMessage) => {
        const text = (retry ? retry.text : message).trim();
        const media = retry ? retry.media : attachment ?? undefined;
        if ((!text && !media) || !selectedPhone) return;

        const requestId = requestIdRef.current;
        const phone = selectedPhone;

        // Temporary id bigger than any real id → always shows at the bottom
        const tempId = retry?.id ?? Date.now();
        const now = new Date();

        const tempMessage: ChatUIMessage = {
            id: tempId,
            text,
            type: "sent",
            date: now,
            time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "pending",
            media,
        };

        if (!retry) {
            setMessage("");
            setAttachment(null);
        }

        scrollModeRef.current = "bottom";
        setMessages((prev) =>
            mergeMessages(prev.filter((m) => m.id !== tempId), [tempMessage])
        );

        try {
            await dispatch(
                sendWhatsappMessage({
                    mobile: phone,
                    message: text,
                    type: media ? toApiType(media.kind) : "TEXT",
                    mediaId: media ? media.mediaId || media.mediaUrl || "" : "",
                    mediaUrl: media?.mediaUrl || "",
                })
            ).unwrap();

            if (requestId !== requestIdRef.current) return; // switched chat

            try {
                await refreshLatest(tempId);
            } catch {
                // Sent fine but reload failed — show it as sent (one gray tick)
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === tempId ? { ...m, status: undefined, delivery: "sent" as const } : m
                    )
                );
            }
        } catch (error) {
            console.error("Failed to send message", error);
            if (requestId !== requestIdRef.current) return;

            setMessages((prev) =>
                prev.map((m) => (m.id === tempId ? { ...m, status: "failed" as const } : m))
            );
        }

        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sends, Shift+Enter = new line (same as WhatsApp Web)
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Show the "jump to latest" button once the user scrolls up a bit
    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 300);
    };

    const scrollToBottom = () => {
        const el = scrollRef.current;
        el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    };

    const canSend = !!message.trim() || !!attachment;

    /* ---------- Empty state (no contact) ---------- */
    if (!selectedPhone) {
        return (
            <div className="relative flex-1 min-w-0 flex flex-col items-center justify-center bg-[#f7f8fa] px-6 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.06] pointer-events-none bg-repeat"
                    style={{ backgroundImage: `url(${whtsappBg})`, backgroundSize: "412px" }}
                />

                <div className="relative flex flex-col items-center">
                    <div className="w-28 h-28 rounded-full bg-[#d9fdd3] flex items-center justify-center mb-8 shadow-[0_0_0_14px_rgba(217,253,211,0.45)]">
                        <img src={whtsappIcon} alt="WhatsApp" className="w-14 h-14" />
                    </div>
                    <h2 className="text-[30px] font-light text-[#41525d]">WhatsApp Live Chat</h2>
                    <p className="text-[14px] leading-[22px] text-[#667781] mt-3 text-center max-w-[440px]">
                        Pick a conversation from the list to read the chat history and reply to your
                        customers in real time.
                    </p>
                </div>

                <p className="absolute bottom-8 flex items-center gap-1.5 text-[13px] text-[#8696a0]">
                    <FaLock className="text-[10px]" /> Messages are sent via WhatsApp Business
                </p>
            </div>
        );
    }

    return (
        <div className="flex-1 min-w-0 flex flex-col">
            {/* Header */}
            <div className="h-[64px] flex-shrink-0 flex items-center gap-3 px-4 bg-white border-b border-[#e9edef] z-10">
                <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor(selectedPhone)}`}
                >
                    <FaUser className="text-[16px] opacity-80" />
                </div>

                <div className="min-w-0 flex-1">
                    <p className="text-[16px] font-medium text-[#111b21] truncate leading-tight">
                        {formatPhone(selectedPhone)}
                    </p>
                    <p
                        className="flex items-center gap-1.5 text-[12.5px] text-[#667781] leading-tight mt-1"
                        aria-live="polite"
                    >
                        {live === "live" ? (
                            <>
                                <span className="relative flex w-2 h-2">
                                    <span className="absolute inset-0 rounded-full bg-[#25d366] opacity-60 animate-ping" />
                                    <span className="relative w-2 h-2 rounded-full bg-[#25d366]" />
                                </span>
                                Live · new messages appear instantly
                            </>
                        ) : live === "connecting" ? (
                            <>
                                <span className="w-2 h-2 rounded-full bg-[#8696a0]" />
                                Connecting…
                            </>
                        ) : (
                            <>
                                <span className="w-2 h-2 rounded-full bg-[#f59e0b]" />
                                Reconnecting… new messages will show when it's back
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Messages */}
            <div className="relative flex-1 min-h-0 bg-[#efeae2]">
                {/* Doodle wallpaper (fixed behind the scrolling messages) */}
                <div
                    className="absolute inset-0 opacity-[0.4] pointer-events-none bg-repeat"
                    style={{ backgroundImage: `url(${whtsappBg})`, backgroundSize: "412px" }}
                />

                <div ref={scrollRef} onScroll={handleScroll} className="wa-scroll absolute inset-0 overflow-y-auto">
                    <div className="px-[5%] lg:px-[7%] py-3 min-h-full flex flex-col">
                        {/* Top trigger for loading older messages */}
                        {hasOlder && (
                            <div ref={topSentinelRef} className="h-10 flex items-center justify-center flex-shrink-0">
                                {loadingOlder && (
                                    <span className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center">
                                        <span className="w-4 h-4 rounded-full border-2 border-[#00a884] border-t-transparent animate-spin" />
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Notice at the start of the chat (like WhatsApp's encryption banner) */}
                        {!hasOlder && !loading && (
                            <div className="flex justify-center mt-2 mb-1 flex-shrink-0">
                                <p className="max-w-[480px] text-center bg-[#ffeecd] text-[#54656f] text-[12.5px] leading-[18px] rounded-lg px-3 py-1.5 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                                    <FaLock className="inline text-[9px] mr-1.5 -mt-0.5" />
                                    Messages in this chat are sent through the WhatsApp Business Platform.
                                </p>
                            </div>
                        )}

                        {loading && messages.length === 0 ? (
                            <div className="m-auto flex items-center gap-2 text-[13px] text-[#54656f] bg-white rounded-full px-4 py-2 shadow-sm">
                                <span className="w-4 h-4 rounded-full border-2 border-[#00a884] border-t-transparent animate-spin" />
                                Loading messages…
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="m-auto text-center bg-white text-[#54656f] text-[13px] rounded-xl px-5 py-3 shadow-sm max-w-[360px]">
                                No messages yet. Say hello 👋 to start the conversation.
                            </div>
                        ) : (
                            <>
                                {/* Push short chats to the bottom like WhatsApp */}
                                <div className="flex-1" />

                                {rows.map((row) =>
                                    row.kind === "date" ? (
                                        <div key={row.key} className="sticky top-2 z-[1] flex justify-center my-3">
                                            <span className="bg-white/95 text-[#54656f] text-[12px] font-medium tracking-wide px-3 py-1.5 rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                                                {row.label}
                                            </span>
                                        </div>
                                    ) : (
                                        <div
                                            key={row.key}
                                            className={`flex ${row.msg.type === "sent" ? "justify-end" : "justify-start"
                                                } ${row.first ? "mt-3" : "mt-[2px]"}`}
                                        >
                                            <div
                                                className={`relative max-w-[75%] lg:max-w-[65%] px-[9px] pt-1.5 pb-2 rounded-[7.5px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${row.msg.type === "sent" ? "bg-[#d9fdd3]" : "bg-white"
                                                    } ${row.first
                                                        ? row.msg.type === "sent"
                                                            ? "rounded-tr-none"
                                                            : "rounded-tl-none"
                                                        : ""
                                                    } ${row.msg.status === "pending" ? "opacity-80" : ""}`}
                                            >
                                                {row.first && (
                                                    <Tail side={row.msg.type === "sent" ? "right" : "left"} />
                                                )}

                                                {/* Image / video / audio / document */}
                                                {row.msg.media && <ChatMedia media={row.msg.media} />}

                                                {row.msg.text ? (
                                                    <>
                                                        <span className="text-[14.2px] leading-[19px] text-[#111b21] whitespace-pre-wrap break-words">
                                                            {row.msg.text}
                                                        </span>

                                                        {/* Spacer so time never overlaps text */}
                                                        <span
                                                            className={`inline-block ${row.msg.type === "sent" ? "w-[74px]" : "w-[56px]"}`}
                                                        />
                                                    </>
                                                ) : (
                                                    // Media without caption: room for the time below it
                                                    <div className="h-3" />
                                                )}

                                                <span className="absolute right-[7px] bottom-[5px] flex items-center gap-1 text-[11px] leading-none text-[#667781]">
                                                    {row.msg.time}
                                                    {row.msg.type === "sent" && <Ticks msg={row.msg} />}
                                                </span>

                                                {row.msg.status === "failed" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSend(row.msg)}
                                                        className="flex items-center gap-1 mt-1 text-[11px] font-medium text-[#ea0038] hover:underline"
                                                    >
                                                        <FaExclamationCircle className="text-[11px]" />
                                                        Not sent · Tap to retry
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Jump to latest message */}
                {showScrollDown && (
                    <button
                        type="button"
                        onClick={scrollToBottom}
                        aria-label="Scroll to latest message"
                        className="absolute right-5 bottom-4 w-[42px] h-[42px] rounded-full bg-white text-[#54656f] shadow-[0_1px_3px_rgba(11,20,26,0.26)] flex items-center justify-center hover:text-[#111b21] transition"
                    >
                        <FaChevronDown className="text-[14px] mt-0.5" />
                    </button>
                )}
            </div>

            {/* Composer */}
            <div className="flex-shrink-0 bg-[#f0f2f5] px-3 py-2.5">
                {/* Selected attachment preview */}
                {attachment && (
                    <div className="flex items-center gap-3 mb-2 p-2 pr-3 bg-white rounded-2xl shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#f0f2f5] flex items-center justify-center flex-shrink-0">
                            {attachment.kind === "image" ? (
                                <img src={attachment.mediaUrl || ""} alt="" className="w-full h-full object-cover" />
                            ) : attachment.kind === "video" ? (
                                <video src={attachment.mediaUrl || ""} muted className="w-full h-full object-cover" />
                            ) : (
                                <FaFileAlt className="text-[22px] text-[#8696a0]" />
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-medium text-[#111b21] truncate">
                                {attachment.fileName || "Attachment"}
                            </p>
                            <p className="text-[12px] text-[#667781] capitalize">
                                {attachment.kind} · caption optional
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => setAttachment(null)}
                            aria-label="Remove attachment"
                            className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-[#54656f] hover:bg-[#f0f2f5]"
                        >
                            <FaTimes className="text-[14px]" />
                        </button>
                    </div>
                )}

                <div className="flex items-end gap-2">
                    {/* Input pill: attach + textarea */}
                    <div className="flex-1 min-w-0 flex items-end bg-white rounded-[24px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                        {/* Attach → Gallery */}
                        <button
                            type="button"
                            onClick={openGallery}
                            title="Attach from gallery"
                            aria-label="Attach"
                            className="w-[44px] h-[44px] flex-shrink-0 rounded-full flex items-center justify-center text-[#54656f] hover:text-[#00a884] transition"
                        >
                            <FaPaperclip className="text-[18px] -rotate-45" />
                        </button>

                        <textarea
                            ref={inputRef}
                            rows={1}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={attachment ? "Add a caption" : "Type a message"}
                            className="wa-scroll flex-1 min-w-0 resize-none bg-transparent pr-4 py-[11px] text-[15px] leading-[22px] text-[#111b21] placeholder-[#667781] outline-none max-h-[120px]"
                        />
                    </div>

                    <button
                        type="button"
                        onClick={() => handleSend()}
                        disabled={!canSend}
                        aria-label="Send"
                        className={`w-[44px] h-[44px] flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-150 ${canSend
                                ? "bg-[#00a884] text-white hover:bg-[#008f72] shadow-[0_1px_3px_rgba(11,20,26,0.2)] scale-100"
                                : "bg-[#d1d7db] text-white cursor-not-allowed scale-95"
                            }`}
                    >
                        <FaPaperPlane className="text-[16px] -ml-0.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
